import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, Eye, EyeOff, FlaskConical, ArrowRight, Loader2, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { API_BASE } from '@/lib/api-client';
import { consumeReturnPath } from '@/lib/session';
import nnpcLogo from '@/assets/nnpc-logo.png';

const SHOW_DEV_LOGIN =
  import.meta.env.DEV || import.meta.env.VITE_ENABLE_DEV_LOGIN === 'true';

const NNPC_DOMAINS = ['nnpcgroup.com', 'nnpc.com.ng'];

function isNnpcEmail(email: string): boolean {
  const domain = email.trim().toLowerCase().split('@')[1] ?? '';
  return NNPC_DOMAINS.some((d) => domain === d || domain.endsWith(`.${d}`));
}

type AdminEmailMethod = 'sso' | 'password' | 'setup_pending' | 'not_found';

async function checkAdminEmail(email: string): Promise<{ method: AdminEmailMethod; otpRequired?: boolean }> {
  const res = await fetch(`${API_BASE}/api/auth/admin/check-email`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error('Could not verify email. Please try again.');
  return res.json();
}

// ── Dev login panel ───────────────────────────────────────────────────────────

interface DevAdminUser {
  staffId: string | null;
  name: string;
  email: string;
}

async function fetchDevAdminUsers(search: string): Promise<DevAdminUser[]> {
  if (!search.trim()) return [];
  const res = await fetch(
    `${API_BASE}/api/auth/dev-admin-users?search=${encodeURIComponent(search.trim())}`,
    { credentials: 'include' },
  );
  if (!res.ok) return [];
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

async function devImpersonateAdmin(staffId: string): Promise<{ accessToken: string }> {
  const res = await fetch(`${API_BASE}/api/auth/dev-admin-impersonate`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ staffId }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.message ?? 'Impersonation failed');
  }
  return res.json();
}

function DevLoginPanel() {
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState<DevAdminUser[]>([]);
  const [searching, setSearching] = useState(false);
  const [impersonating, setImpersonating] = useState<string | null>(null);
  const [devError, setDevError] = useState<string | null>(null);

  const handleSearch = async () => {
    setSearching(true);
    setDevError(null);
    try {
      const results = await fetchDevAdminUsers(search);
      setUsers(results);
      if (results.length === 0)
        setDevError('No admin users found. Try a different search term.');
    } catch {
      setDevError('Search failed. Ensure ENABLE_DEV_AUTH_ENDPOINTS=true in the API.');
    } finally {
      setSearching(false);
    }
  };

  const handlePick = async (staffId: string | null, email: string | null) => {
    const key = staffId ?? email;
    if (!key) return;
    setImpersonating(key);
    setDevError(null);
    try {
      const { accessToken } = await devImpersonateAdmin(key);
      await loginWithToken(accessToken);
      navigate(consumeReturnPath() ?? '/', { replace: true });
    } catch (err: any) {
      setDevError(err?.message ?? 'Impersonation failed');
    } finally {
      setImpersonating(null);
    }
  };

  return (
    <Card className="border-yellow-500/50 bg-yellow-500/5 shadow-sm">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <FlaskConical className="h-4 w-4 text-yellow-600" />
          <span className="text-sm font-semibold text-yellow-700">Dev Test Login</span>
          <span className="text-xs text-muted-foreground">(dev only)</span>
        </div>
        <p className="text-xs text-muted-foreground">
          Impersonate any admin user without a password.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Input
            placeholder="Search by name, staff ID or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="h-9 text-sm"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleSearch}
            disabled={searching}
            className="border-yellow-500/40"
          >
            {searching ? '…' : 'Search'}
          </Button>
        </div>
        {devError && <p className="text-xs text-destructive">{devError}</p>}
        {users.length > 0 && (
          <div className="max-h-48 divide-y overflow-y-auto rounded-md border text-sm">
            {users.map((u) => (
              <button
                key={u.email}
                type="button"
                disabled={impersonating === (u.staffId ?? u.email)}
                onClick={() => handlePick(u.staffId, u.email)}
                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left transition-colors hover:bg-muted/50 disabled:opacity-60"
              >
                <div>
                  <p className="font-medium">{u.name}</p>
                  <p className="text-xs text-muted-foreground">{u.staffId ?? u.email}</p>
                </div>
                <span className="shrink-0 text-xs text-yellow-600">
                  {impersonating === (u.staffId ?? u.email) ? 'Logging in…' : 'Login as →'}
                </span>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Main login page ───────────────────────────────────────────────────────────

type LoginStep = 'email' | 'password';

export default function LoginPage() {
  const { signInWithMicrosoft, signInWithEmail } = useAuth();
  const navigate = useNavigate();
  const passwordRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<LoginStep>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [emailStatus, setEmailStatus] = useState<AdminEmailMethod | null>(null);
  const [otpRequired, setOtpRequired] = useState(true);
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleMicrosoftSSO = async () => {
    setError(null);
    setSsoLoading(true);
    try {
      await signInWithMicrosoft();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Microsoft sign-in failed');
      setSsoLoading(false);
    }
  };

  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return;

    // NNPC domain → SSO immediately (same as staff login)
    if (isNnpcEmail(trimmedEmail)) {
      setSsoLoading(true);
      try { await signInWithMicrosoft(); } catch { setSsoLoading(false); }
      return;
    }

    setCheckingEmail(true);
    setError(null);
    setEmailStatus(null);
    try {
      const result = await checkAdminEmail(trimmedEmail);
      setEmailStatus(result.method);
      if (result.method === 'sso') {
        setSsoLoading(true);
        try { await signInWithMicrosoft(); } catch { setSsoLoading(false); }
        return;
      }
      if (result.method === 'password') {
        setOtpRequired(result.otpRequired !== false);
        setStep('password');
        setTimeout(() => passwordRef.current?.focus(), 50);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not verify email. Please try again.');
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) return;
    if (otpRequired && otpSent && otp.length !== 6) return;
    setLoading(true);
    setError(null);
    try {
      await signInWithEmail(email.trim(), password, otpRequired && otpSent ? otp.trim() : undefined);
      window.location.href = consumeReturnPath() ?? '/';
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign in failed. Please try again.';
      if (message.toLowerCase().includes('verification code sent')) {
        setOtpSent(true);
        setOtp('');
        setError(null);
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const resetToEmail = () => {
    setStep('email');
    setPassword('');
    setOtp('');
    setEmailStatus(null);
    setError(null);
    setOtpRequired(true);
    setOtpSent(false);
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4 py-8">
      {/* Admin-specific background */}
      <div className="pointer-events-none absolute inset-0">
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 opacity-[0.025] [background-image:linear-gradient(to_right,currentColor_1px,transparent_1px),linear-gradient(to_bottom,currentColor_1px,transparent_1px)] [background-size:40px_40px]" />
        <div className="absolute -left-24 -top-24 h-80 w-80 rounded-full bg-primary/15 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-amber-500/8 blur-3xl" />
        <div className="absolute left-1/2 top-1/4 h-64 w-64 -translate-x-1/2 rounded-full bg-primary/5 blur-3xl" />
      </div>

      <div className="relative w-full max-w-md space-y-6">
        {/* Brand */}
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="relative">
            <img src={nnpcLogo} alt="NNPC Logo" className="h-16 w-auto" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              Executive and Middle Management Resourcing Portal
            </h1>
            <div className="mt-1.5 flex items-center justify-center gap-1.5">
              <Shield className="h-3.5 w-3.5 text-amber-500/70" aria-hidden="true" />
              <p className="text-sm font-medium text-amber-600/80 dark:text-amber-400/80">
                Administration Console
              </p>
            </div>
          </div>
        </div>

        <Card className="shadow-xl shadow-primary/10 border-border/60">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-lg">Sign in to your account</CardTitle>
            <CardDescription>
              {step === 'email'
                ? 'Enter your work email to continue.'
                : `Signing in as ${email}`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Primary SSO button */}
            <Button
              type="button"
              variant="outline"
              className="w-full gap-2"
              onClick={handleMicrosoftSSO}
              disabled={ssoLoading || loading}
            >
              <LogIn className="h-4 w-4" aria-hidden="true" />
              {ssoLoading ? 'Signing you in…' : 'Sign in with NNPC SSO'}
            </Button>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">or</span>
              </div>
            </div>

            {/* Email-first form */}
            {step === 'email' ? (
              <form onSubmit={handleContinue} className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="email">Email address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setEmailStatus(null); setError(null); }}
                    required
                    autoComplete="email"
                    className="h-11"
                    disabled={checkingEmail || ssoLoading}
                  />
                </div>

                {emailStatus === 'setup_pending' && (
                  <p className="text-sm text-amber-600">
                    Your account setup is incomplete. Check your email for an invite link, or ask an admin to resend it.
                  </p>
                )}
                {emailStatus === 'not_found' && (
                  <p className="text-sm text-muted-foreground">
                    No admin account found for this email address.
                  </p>
                )}
                {error && <p className="text-sm text-destructive">{error}</p>}

                <Button
                  type="submit"
                  className="w-full h-11"
                  disabled={checkingEmail || ssoLoading || !email.trim()}
                >
                  {checkingEmail ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Checking…
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Continue
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleLogin} className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground truncate max-w-[260px]">{email}</p>
                  <button
                    type="button"
                    onClick={resetToEmail}
                    className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 shrink-0 ml-2"
                  >
                    Change
                  </button>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      ref={passwordRef}
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                      className="h-11 pr-11"
                      disabled={loading}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full w-11 hover:bg-transparent"
                      onClick={() => setShowPassword((p) => !p)}
                      tabIndex={-1}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                </div>

                {otpRequired && otpSent && (
                  <div className="space-y-2">
                    <Label htmlFor="otp">Verification Code</Label>
                    <Input
                      id="otp"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      placeholder="6-digit code"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      required
                      autoComplete="one-time-code"
                      className="h-11"
                      disabled={loading}
                    />
                    <p className="text-xs text-muted-foreground">
                      A 15-minute login code was sent to your email.
                    </p>
                  </div>
                )}

                {error && <p className="text-sm text-destructive">{error}</p>}

                <Button
                  type="submit"
                  className="w-full h-11"
                  disabled={loading || !password.trim() || (otpRequired && otpSent && otp.length !== 6)}
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Signing in…
                    </span>
                  ) : (
                    otpRequired && !otpSent ? 'Send Verification Code' : 'Sign In'
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground">
          Restricted admin access only. All authentication events are audited.
        </p>

        {SHOW_DEV_LOGIN && <DevLoginPanel />}
      </div>
    </div>
  );
}
